import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { LayoutDashboard, Users, CreditCard, LogOut, Menu, X, Sparkles, Inbox, UserCog, Banknote, DollarSign, Bug, Cog } from "lucide-react";

const adminNavItems = [
  { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/users", label: "Users", icon: Users },
  { path: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard },
  { path: "/admin/pricing", label: "Pricing", icon: DollarSign },
  { path: "/admin/roster-debug", label: "Roster Debug", icon: Bug },
  { path: "/admin/payments", label: "Payments", icon: Banknote },
  { path: "/admin/inbox", label: "Inbox", icon: Inbox },
  { path: "/admin/ai-logs", label: "AI Prompt Logs", icon: Sparkles },
  { path: "/admin/ai-settings", label: "AI Settings", icon: Cog },
  { path: "/admin/profile", label: "Profile", icon: UserCog },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const checkAdmin = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/auth/me`);
      setAdmin(res.data);
    } catch {
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { checkAdmin(); }, [checkAdmin]);

  // Poll for unread message count every 30s while admin is logged in
  useEffect(() => {
    if (!admin) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/admin/contact-messages/unread-count`);
        if (!cancelled) setUnreadCount(r.data?.unread || 0);
      } catch { /* silent — don't spam if temp 401/network */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [admin, location.pathname]);  // re-fetch on nav so badge updates after admin reads inbox

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/auth/logout`, {});
    } catch { /* ignore */ }
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-60 bg-zinc-900 border-r border-zinc-800
        transform transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
            <h1 className="text-lg font-bold text-white tracking-tight">
              roster<span className="text-[#002FA7]">.ai</span>
              <span className="text-xs text-zinc-500 ml-2 font-normal">Admin</span>
            </h1>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-zinc-500 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 py-3">
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isInbox = item.path === "/admin/inbox";
              return (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors text-left ${
                    isActive ? 'text-white bg-zinc-800 border-l-2 border-[#002FA7]' : 'text-zinc-500 hover:text-zinc-300 border-l-2 border-transparent'
                  }`}
                  data-testid={`admin-nav-${item.path.split('/').pop()}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1">{item.label}</span>
                  {isInbox && unreadCount > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold tabular-nums text-white bg-rose-600 rounded-full"
                      data-testid="admin-nav-inbox-badge"
                    >
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="p-4 border-t border-zinc-800">
            <p className="text-xs text-zinc-500 mb-2">{admin?.email}</p>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-zinc-500 hover:text-white hover:bg-zinc-800 rounded transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden flex items-center justify-between p-4 bg-zinc-900 border-b border-zinc-800">
          <button onClick={() => setSidebarOpen(true)} className="text-zinc-400 hover:text-white">
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-sm font-bold text-white">Admin Panel</span>
          <div className="w-5" />
        </header>
        <main className="flex-1 p-6 lg:p-8 overflow-auto">
          <Outlet context={{ admin }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
